/* eslint-disable react/function-component-definition */
import {
  ActionBar,
  ActionType,
  FormContext,
  FormState,
  FormValues,
  InputForm,
  InputFormComponent,
  InputFormGroup,
  SimpleBuilder,
  TextInput,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import { DefaultButton, Icon, Label, Link, mergeStyleSets, PrimaryButton, Stack, Text } from "@fluentui/react";
import { DeleteIconDisabledSvg, DeleteIconSvg } from "../../ApplicationIcons";
import * as Messages from "../../codegen/Messages";
import { tagsLearnMoreUrl } from "../../constants/docConstants";
import { areTagsEqual } from "../../helpers/resourceHelper";
import { validateTagKey } from "../../helpers/validationHelper";

interface TagDetailsProps {
  tagsDefaultValue: { name: string, value: string }[];
  disabled?: boolean;
  onApply?: (formValues: FormValues) => void;
}

export const enum Fields {
  Tags = "tags",
  Name = "name",
  Value = "value",
}

const classNames = mergeStyleSets({
  nameInput: { width: "50%", minWidth: "107px" },
  valueInput: { width: "50%", minWidth: "107px" },
});

interface TagListBuilderProps {
  tagsDefaultValue: { name: string, value: string }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFieldValueRef?: React.MutableRefObject<((formFieldValue: any) => void | undefined) | undefined>;
}

const TagsListBuilder = ({ tagsDefaultValue, setFieldValueRef }: TagListBuilderProps): JSX.Element => {
  const form: FormState = React.useContext(FormContext);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setFieldValue = (formFieldValue: any): void => {
    const formValues: FormValues = form.getGroupValues(InputFormGroup);
    Object.keys(formValues || {}).forEach(value => {
      form.unRegisterField(value, InputFormGroup);
      form.deleteField(value, InputFormGroup);
    });
    form.setValue(formFieldValue, Fields.Tags, InputFormGroup);
  };

  React.useEffect(() => {
    if (setFieldValueRef) {
      setFieldValueRef.current = setFieldValue;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack style={{ paddingBottom: "20px" }}>
      <SimpleBuilder
        fieldName={Fields.Tags}
        defaultValue={tagsDefaultValue}
      >
        <TextInput
          label={Messages.common.name()}
          fieldName={Fields.Name}
          className={classNames.nameInput}
          validator={validateTagKey}
        />
        <Label>:</Label>
        <TextInput
          label={Messages.common.value()}
          fieldName={Fields.Value}
          className={classNames.valueInput}
        />
      </SimpleBuilder>
    </Stack>
  );
};

export function TagDetails({
  tagsDefaultValue,
  disabled,
  onApply,
}: TagDetailsProps): JSX.Element {
  const formComponentRef = React.useRef<InputFormComponent>();
  const setFormComponentRef = (formComponent: InputFormComponent): void => {
    formComponentRef.current = formComponent;
  };
  const [key, setKey] = React.useState<string>(uniqueGUID());
  const [valueChanged, setValueChanged] = React.useState<boolean>(false);
  const [formTagFieldValue, setFormTagFieldValue] = React.useState<[]>();
  const [tagsBuilderDefault, setTagsBuilderDefault] = React.useState<{
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
  }[]>(tagsDefaultValue);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setFieldValueRef = React.useRef<(value: any) => void | undefined>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onRenderTagIcon = (iconProps: any): JSX.Element => (iconProps.disabled ? DeleteIconDisabledSvg : DeleteIconSvg);

  const tagsActionItems: ActionType[] = [
    {
      key: "delete",
      text: Messages.actions.deleteAll(),
      icon: "delete-Svg",
      disabled,
      onRenderIcon: onRenderTagIcon,
      onClick: () => {
        onDeleteAll();
      },
    },
  ];

  const onSubmit = (formValues: FormValues): void => {
    if (onApply) {
      onApply(formValues);
    }
  };

  const onClick = (): void => {
    formComponentRef.current?.enableValidation();
    formComponentRef.current?.submit();
  };

  const onDiscardChanges = (): void => {
    setTagsBuilderDefault(tagsDefaultValue);
    if (setFieldValueRef && setFieldValueRef.current) {
      setFieldValueRef.current(tagsDefaultValue);
    }
    setKey(uniqueGUID());
    setValueChanged(false);
  };

  const validateValueChanged = (
    fieldValue: { name: string, value: string }[],
    defaultFieldValue: { name: string, value: string }[],
  ): void => {
    const compareResult = areTagsEqual(fieldValue, defaultFieldValue);
    if (compareResult) {
      setValueChanged(false);
    } else {
      setValueChanged(true);
    }
  };

  const onDeleteAll = (): void => {
    setTagsBuilderDefault([]);
    if (setFieldValueRef && setFieldValueRef.current) {
      setFieldValueRef.current([]);
    }
    setKey(uniqueGUID());
  };

  React.useEffect(() => {
    validateValueChanged(formTagFieldValue || [], tagsDefaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formTagFieldValue]);

  return (
    <InputForm
      onSubmit={onSubmit}
      componentRef={setFormComponentRef}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onFormChange={(_formValues: FormValues, fieldValue: any, field: string) => {
        if (field === Fields.Tags) {
          setFormTagFieldValue(fieldValue);
        }
      }}
    >
      <ActionBar actions={tagsActionItems} />
      <Stack style={{ margin: "10px 0px" }}>
        <Text>{Messages.tags.description()}</Text>
        <Link href={tagsLearnMoreUrl} target="_blank">
          <>
            <span>{Messages.tags.info.learnMore()}</span>
            <Icon style={{ paddingLeft: "5px" }} iconName="OpenInNewWindow" />
          </>
        </Link>
      </Stack>
      <br />
      <div>
        <Text>{Messages.tags.note()}</Text>
      </div>
      <br />
      <TagsListBuilder
        tagsDefaultValue={tagsBuilderDefault}
        setFieldValueRef={setFieldValueRef}
        key={key}
      />
      <div style={{ display: "flex", position: "fixed", bottom: "0px", padding: "20px", width: "100%" }}>
        <Stack horizontal tokens={{ childrenGap: 10 }}>
          <PrimaryButton
            style={{ height: 25 }}
            text={Messages.actions.apply()}
            disabled={disabled || !valueChanged}
            onClick={onClick}
          />
          <DefaultButton
            style={{ height: 25 }}
            text={Messages.actions.discardChanges()}
            disabled={disabled || !valueChanged}
            onClick={onDiscardChanges}
          />
        </Stack>
      </div>
    </InputForm>
  );
}
